<template lang="pug">
.container
    .chart-container(v-if="seriesData.length > 0")
        chart(:options="flowChartOptions")
        .modal-overlay(v-if="showTableModal" @click.self="showTableModal = false")
            .modal-content
                button.close(@click="showTableModal = false") ×
                h3 Series Data Table
                    v-icon.copy-btn(@click="copyTableToClipboard") content_copy
                table.table()
                    thead
                        tr
                            th Hour
                            th(v-for="series in seriesData" :key="series.name") {{ series.name }}
                    tbody
                        tr(v-for="(hour, idx) in 24" :key="idx")
                            td {{ formatHour(idx) }}
                            td(v-for="series in seriesData" :key="series.name")
                                | {{ series.data[idx] ?? '-' }}
    .no-data(v-else)
        v-card(light rounded class='mt-2')
            v-card-title(class="brand white--text") {{name || 'No Data Available'}}
            v-card-text No data available to display the chart.

</template>

<script lang="js">

import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import exportingInit from 'highcharts/modules/exporting';
import {cloneDeep} from 'lodash';

exportingInit(Highcharts);

export default {
    name: 'FlowChart',
    components: {
        chart: Chart
    },
    props: {
        seriesData: {
            type: Array,
            required: false,
            default: () => []
        },
        productionTime: {
            type: Number,
            required: false,
            default: null
        },
        productionVolume: {
            type: Number,
            required: false,
            default: null
        },
        startDate: {
            type: String,
            required: false,
            default: ''
        },
        name: {
            type: String,
            required: false,
        },
        isHistorical: {
            type: Boolean,
            required: false,
            default: false
        },
        productionVolumeUnit: {
            type: String,
            required: false,
            default: ''
        },
        yMaxRate: {
            type: Number,
            required: false,
            default: null
        },
        yMaxVolume: {
            type: Number,
            required: false,
            default: null
        },
    },
    data () {
        const highchartsColors = Highcharts.getOptions().colors;
        return {
            flowChartOptions: {
                credits: { enabled: false },
                colors: ['#058DC7', '#50B432', '#ED561B', '#754087', '#24CBE5', '#64E572'],
                chart: {
                    type: 'line',
                    backgroundColor: '#f5f5f5', // Light gray for subtle contrast
                    plotBorderWidth: 1,
                    plotBorderColor: '#E0E0E0', // Faint border for definition
                    shadow: {
                        color: 'rgba(0, 0, 0, 0.2)',  // Color of the shadow
                        offsetX: 3,                  // Horizontal offset of the shadow
                        offsetY: 3,                  // Vertical offset of the shadow
                        opacity: 0.5,                // Opacity of the shadow
                        width: 5                     // Blur width of the shadow
                    },
                    spacing: [30, 30, 30, 30],
                    borderRadius: 10,
                    borderWidth: 1,                 // Width of the border in pixels
                    borderColor: '#d1d1d1',
                    alignThresholds: true,
                },
                title: {
                    text: '',
                    align: 'left'
                },
                yAxis: [
                    {
                        title: {
                            text: 'Rate'
                        },

                    },
                    {
                        title: {
                            text: 'Volume'
                        },
                        opposite: true
                    },
                ],
                xAxis: {
                    categories: Array.from({ length: 24 }, (_, i) => i),
                    labels: {
                        formatter: function () {
                            let hour = this.value;
                            let amPm = hour < 12 ? 'AM' : 'PM';
                            hour = hour % 12;
                            hour = hour || 12;
                            return `${hour} ${amPm}`;
                        }
                    }
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    backgroundColor: '#ffffff', // White background for better contrast
                    borderColor: '#cccccc',
                    borderRadius: 10,
                    shadow: true,
                    style: {
                        color: '#333333',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        let hour = this.x;
                        let amPm = hour < 12 ? 'AM' : 'PM';
                        hour = hour % 12;
                        hour = hour || 12;
                        let s = `<h3 style="margin: 2px 0;">Time: ${hour} ${amPm}</h3><br>`;
                        s += '<table style="width:100%; text-align: left;">';
                        this.points.forEach(function (point) {
                            let seriesName = point.series.name
                                .toLowerCase()
                                .split(' ')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                                .trim();

                            s += `<tr>` +
                                `<td>` +
                                    `<span style="color:${point.color}; font-size: 14px;"><strong>${seriesName}</strong></span>` +
                                `</td>` +
                                `<td style="text-align: right; padding-left: 10px;">` +
                                    `<b>${point.y}</b>` +
                                `</td>` +
                            `</tr>`;
                        });
                        s += '</table>';
                        return s;
                    }
                },
                legend: {
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'bottom',
                    backgroundColor: '#ffffff',
                    borderRadius: 5,
                    shadow: true,
                    labelFormatter: function () {
                        // Convert the series name to Title Case
                        return this.name
                            .toLowerCase()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                            .trim();
                    }
                },
                plotOptions: {
                    series: {
                        marker: {
                            enabled: false
                        },
                        lineWidth: 1.5,
                        animation: {
                            duration: 1500,
                            easing: 'easeOutBounce'
                        },
                        states: {
                            hover: {
                                lineWidthPlus: 2.5
                            }
                        }
                    }
                },
                series: [],
                exporting: {
                    fallbackToExportServer: false,
                    enabled: true,
                    buttons: {
                        contextButton: {
                            menuItems: [
                                'viewFullscreen',
                                'printChart',
                                'separator',
                                {
                                    text: 'Show Data Table',
                                    onclick: () => {
                                        if (this.self) this.self.showTableModal = true;
                                    }
                                },
                                {
                                    text: 'Toggle Rate yMax',
                                    onclick: () => {
                                        if (this.self) this.self.toggleYMax(0);
                                    }
                                },
                                {
                                    text: 'Toggle Volume yMax',
                                    onclick: () => {
                                        if (this.self) this.self.toggleYMax(1);
                                    }
                                }
                            ]
                        }
                    }
                }
            },
            showTableModal: false,
            self: this,
        };
    },
    watch: {
        seriesData: {
            handler (newVal, oldVal) {
                this.$set(this.flowChartOptions, 'series', newVal);
            },
            deep: true
        },
        productionTime () {
            this.updateSubtitle();
        },
        productionVolume () {
            this.updateSubtitle();
        },
        startDate () {
            this.updateSubtitle();
        }
    },
    methods: {
        init () {
            const titleAddendum = this.isHistorical ? '(actual)' : '(prediction)';
            this.flowChartOptions.title.text = `${this.name} ${titleAddendum}`;
            this.flowChartOptions.yAxis[0].max = this.yMaxRate;
            this.flowChartOptions.yAxis[1].max = null;
            if (this.seriesData?.length > 0) {
                this.$set(this.flowChartOptions, 'series', this.seriesData);
            } else {
                this.$set(this.flowChartOptions, 'series', []);
            }
            this.updateSubtitle();
        },
        updateSubtitle () {
            let subtitleText = this.startDate ? `Date: ${this.startDate}` : '';
            if (this.productionTime != null || this.productionVolume != null) {
                subtitleText += `<br>Production Volume: <span style="font-weight:bolder;">${this.productionVolume || 0} ${this.productionVolumeUnit}</span>`;
            }
            this.flowChartOptions.subtitle = {
                text: subtitleText,
                align: 'left'
            };
        },

        formatHour (hour) {
            let amPm = hour < 12 ? 'AM' : 'PM';
            let h = hour % 12;
            h = h || 12;
            return `${h} ${amPm}`;
        },

        copyTableToClipboard () {
            // Start with the header row
            let rows = [];
            let header = ['Hour', ...this.seriesData.map(s => s.name)];
            rows.push(header);

            // Add each row of data
            for (let i = 0; i < 24; i++) {
                let row = [this.formatHour(i)];
                for (const series of this.seriesData) {
                    row.push(series.data[i] ?? '-');
                }
                rows.push(row);
            }

            // Convert to TSV (tab-separated)
            let tsv = rows.map(r => r.join('\t')).join('\n');

            // Copy to clipboard (async Clipboard API)
            navigator.clipboard.writeText(tsv)
                .then(() => {
                    this.$emit('notify', 'Copied table to clipboard!');
                })
                .catch(err => {
                    alert('Failed to copy: ' + err);
                });
        },

        toggleYMax (axisIndex = 0) {
            const yMax = axisIndex === 0 ? this.yMaxRate : this.yMaxVolume;
            if (this.flowChartOptions.yAxis[axisIndex].max !== null) {
                // Disable yMax
                this.flowChartOptions.yAxis[axisIndex].max = null;
            } else {
                // Enable yMax using the prop value
                this.flowChartOptions.yAxis[axisIndex].max = yMax;
            }
            this.flowChartOptions = cloneDeep(this.flowChartOptions);
        },


    },
    mounted () {
        this.init();
    },
};

</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.modal-content {
    background: #fff;
    padding: 24px 32px;
    border-radius: 8px;
    min-width: 350px;
    max-width: 90vw;
    max-height: 80vh;
    overflow: auto;
    position: relative;
}
button.close {
    position: absolute;
    top: 8px;
    right: 12px;
    background: none;
    border: none;
    font-size: 1.5em;
    cursor: pointer;
}
.table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
}
.table th, .table td, h3 {
    border: 1px solid #ddd;
    padding: 6px 10px;
    text-align: center;
    color: #060b33;
}
.table th {
    background: #f5f5f5;
}
.copy-btn {
    background: #fff;
    border-radius: 5px;
    color: #222;
    padding: 5px 14px;
    font-size: 1em;
    cursor: pointer;
    margin-bottom: 8px;
    float: right;
}
.copy-btn:hover {
    background: rgb(197, 195, 195);
}

</style>
